import React, { useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  SxProps,
  Theme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/ja";
import { FORMAT_DATE_JA, DASH_FORMAT_DATE } from "constants/format";

/**
 * CalendarIconComponent renders a calendar icon image.
 * This component is used as a custom icon for date pickers.
 */
function CalendarIcon() {
  return <img src="/img/icons/calendar.svg" alt="calendar-icon" style={{ padding: "0px" }} />;
}

dayjs.locale("ja");

// Interface for select options
export interface SelectOption {
  label: string;
  value: string | number;
}

// Interface for autocomplete options
export interface AutocompleteOption {
  label: string;
  value: string | number;
}

// Type guards for safer narrowing
const isAutocompleteOption = (v: unknown): v is AutocompleteOption =>
  !!v && typeof v === "object" && "label" in (v as Record<string, unknown>);

const isDate = (v: unknown): v is Date => v instanceof Date;

// Convert runtime value to display string for text mode
const getDisplayText = (v: string | number | Date | AutocompleteOption | null): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (isDate(v)) return new Intl.DateTimeFormat("ja-JP").format(v);
  if (isAutocompleteOption(v)) return v.label;
  return "";
};

// Main component props interface
export interface LabelValueFieldProps {
  label: string | React.ReactNode;
  value: string | number | Date | AutocompleteOption | null;
  type?: "text" | "input" | "select" | "autocomplete" | "date";
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;

  // Styling props
  labelSx?: SxProps<Theme>;
  valueSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;

  // Select specific props
  options?: SelectOption[];

  // Autocomplete specific props
  autocompleteOptions?: AutocompleteOption[];
  freeSolo?: boolean;

  inputType?: "text" | "number" | "email" | "password";

  // Date specific props
  minDate?: Date | string | null;
  maxDate?: Date | string | null;

  // Event handlers
  onChange?: (value: string | number | Date | AutocompleteOption | null) => void;
  onBlur?: (value: string | number | Date | AutocompleteOption | null) => void;
  onFocus?: () => void;

  // Error state
  error?: boolean;
}

/**
 * SearchItem Component
 *
 * A flexible component that displays a label with yellow background and different types of values:
 * - text: Static text display
 * - input: Editable input field with validation
 * - select: Dropdown selection
 * - autocomplete: Autocomplete input field
 * - date: Date picker input field
 * @param props - Component props
 */
function SearchItem({
  label,
  value,
  type = "text",
  placeholder,
  disabled = false,
  readOnly = false,
  labelSx = {},
  valueSx = {},
  containerSx = {},
  options = [],
  autocompleteOptions = [],
  freeSolo = false,
  inputType = "text",
  minDate,
  maxDate,
  onChange,
  onBlur,
  onFocus,
  error = false,
}: LabelValueFieldProps) {
  // Default styling for label with yellow background
  const defaultLabelSx: SxProps<Theme> = {
    backgroundColor: "var(--primary-500)",
    color: "var(--black)",
    // padding: "10px",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: { xs: 16, md: 18, lg: 20 },
    borderRadius: "0",
    minHeight: "49px",
    minWidth: "205px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    ...labelSx,
  };

  const defaultValueSx: SxProps<Theme> = {
    backgroundColor: "#fff",
    border: "1px solid var(--gray-100)",
    height: "44px",
    padding: "10px",
    fontSize: { xs: 16, md: 18, lg: 20 },
    maxWidth: "100%",
    ...valueSx,
  };

  const defaultContainerSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    minWidth: { xs: "350px", md: "450px" },
    flexWrap: "wrap",
    ...containerSx,
  };

  // Handle input change
  const handleInputChange = useCallback(
    (newValue: string | number | Date | AutocompleteOption | null) => {
      onChange?.(newValue);
    },
    [onChange],
  );

  // Handle blur event
  const handleBlur = useCallback(
    (newValue: string | number | Date | AutocompleteOption | null) => {
      onBlur?.(newValue);
    },
    [onBlur],
  );

  // Render value based on type
  const renderValue = () => {
    switch (type) {
      case "input":
        // Only allow string/number for input type
        if (typeof value !== "string" && typeof value !== "number") {
          return (
            <Box sx={{ ...defaultValueSx, padding: "8px 12px" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: "inherit", color: "#d32f2f" }}>
                Invalid value type for input
              </Typography>
            </Box>
          );
        }

        return (
          <TextField
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            InputProps={{
              readOnly,
            }}
            type={inputType}
            variant="outlined"
            size="small"
            fullWidth
            onChange={(e) => handleInputChange(e.target.value)}
            onBlur={(e) => handleBlur(e.target.value)}
            onFocus={onFocus}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "0",
                backgroundColor: disabled ? "var(--offwhite-300)" : "#fff",
                height: "44px",
              },
              "& .MuiOutlinedInput-input": {
                textAlign: "left",
                padding: "10.5px 14px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid var(--gray-100)",
              },
              ...valueSx,
            }}
          />
        );

      case "select":
        // Only allow string/number for select type
        if (typeof value !== "string" && typeof value !== "number") {
          return (
            <Box sx={{ ...defaultValueSx, padding: "8px 12px" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: "inherit", color: "#d32f2f" }}>
                Invalid value type for select
              </Typography>
            </Box>
          );
        }

        return (
          <FormControl fullWidth size="small">
            <Select
              value={value}
              disabled={disabled}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={(e) => handleBlur(e.target.value as string)}
              onFocus={onFocus}
              sx={{
                borderRadius: "0",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  height: "44px",
                },
                "& .MuiSelect-select": {
                  textAlign: "center",
                  padding: "10.5px 14px",
                  fontSize: "14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid var(--gray-100)",
                },
                ...valueSx,
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case "autocomplete": {
        // Normalize value: string for freeSolo, option object otherwise
        const normalizedValue = !value
          ? null
          : typeof value === "string"
            ? freeSolo
              ? value
              : autocompleteOptions.find((opt) => opt.label === value || opt.value === value) || null
            : (value as AutocompleteOption);

        const getCurrentValueStr = () =>
          typeof value === "string" ? value : (value as AutocompleteOption)?.label || "";

        return (
          <Autocomplete
            value={normalizedValue}
            inputValue={freeSolo && typeof value === "string" ? value : undefined}
            options={autocompleteOptions}
            freeSolo={freeSolo}
            disabled={disabled}
            readOnly={readOnly}
            onChange={(_, newValue) => handleInputChange(newValue)}
            onInputChange={(_, newInputValue, reason) => {
              if (freeSolo && reason === "input" && newInputValue) {
                handleInputChange(newInputValue);
              }
            }}
            onBlur={(e) => {
              if (freeSolo) {
                const blurValue = (e.target as HTMLInputElement).value?.trim();
                if (blurValue && blurValue !== getCurrentValueStr()) {
                  handleInputChange(blurValue);
                }
              }
              handleBlur(value);
            }}
            onFocus={onFocus}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
            isOptionEqualToValue={(option, selected) => {
              if (typeof option === "string" || typeof selected === "string") {
                return option === selected;
              }
              return option.value === selected.value;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                error={error}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0",
                    backgroundColor: "#fff",
                    height: "44px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: error ? "var(--red-800)" : undefined,
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "10.5px 14px",
                    fontSize: { xs: 16, md: 18, lg: 20 },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: error ? "1px solid var(--red-800)" : "1px solid var(--gray-100)",
                  },
                  ...valueSx,
                }}
              />
            )}
          />
        );
      }

      case "date": {
        // Handle date value - support both Date object and string format
        const dateValue = (() => {
          if (!value) return null;
          if (value instanceof Date) {
            // Create dayjs with local time, start of day to avoid timezone issues
            return dayjs(value).startOf("day");
          }
          if (typeof value === "string") {
            // Parse string date using configured dash format
            return dayjs(value, DASH_FORMAT_DATE);
          }
          return null;
        })();

        const dateMinDate = minDate
          ? typeof minDate === "string"
            ? dayjs(minDate, DASH_FORMAT_DATE).startOf("day")
            : dayjs(minDate).startOf("day")
          : undefined;
        const dateMaxDate = maxDate
          ? typeof maxDate === "string"
            ? dayjs(maxDate, DASH_FORMAT_DATE).startOf("day")
            : dayjs(maxDate).startOf("day")
          : undefined;

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
            <DatePicker
              value={dateValue}
              disabled={disabled}
              readOnly={readOnly}
              minDate={dateMinDate}
              maxDate={dateMaxDate}
              onChange={(newValue: Dayjs | null) => {
                if (newValue) {
                  // Format directly to string to avoid timezone conversion issues
                  const dateString = newValue.format(DASH_FORMAT_DATE);
                  handleInputChange(dateString);
                } else {
                  handleInputChange(null);
                }
              }}
              slots={{ openPickerIcon: CalendarIcon }}
              desktopModeMediaQuery="@media (min-width: 0px)"
              format={FORMAT_DATE_JA}
              slotProps={{
                textField: {
                  placeholder: placeholder || FORMAT_DATE_JA,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0",
                      backgroundColor: "#fff",
                      height: "44px",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "10px",
                      fontSize: { xs: 16, md: 18, lg: 20 },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "1px solid var(--gray-100)",
                    },
                    "& .MuiIconButton-root": {
                      padding: "0",
                    },
                    ...valueSx,
                  },
                },
              }}
            />
          </LocalizationProvider>
        );
      }

      case "text":
      default:
        return (
          <Box sx={{ ...defaultValueSx, padding: "8px 12px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: "inherit" }}>
              {getDisplayText(value)}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Box sx={defaultContainerSx}>
      {/* Label with yellow background */}
      <Box sx={defaultLabelSx}>
        {typeof label === "string" ? (
          <Typography sx={{ fontSize: "inherit", fontWeight: "inherit", color: "inherit" }}>
            {label}
          </Typography>
        ) : (
          label
        )}
      </Box>

      {/* Value - wrapped in a flex container to display horizontally */}
      <Box sx={{ flex: 1, minWidth: 0 }}>{renderValue()}</Box>
    </Box>
  );
}

export default SearchItem;
